import { useParams, Link, useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import { useLang } from '@/context/LanguageContext';
import { motion } from 'framer-motion';
import { ArrowLeft, ArrowRight, ArrowUpRight, Clock, Calendar, User } from 'lucide-react';
import { projectsData } from '@/data/projectsData';
import { Helmet } from 'react-helmet-async';

export default function ProjectDetail() {
  const { slug } = useParams();
  const { lang, t, toggleLang } = useLang();
  const navigate = useNavigate();

  const projectIndex = projectsData.findIndex(p => p.slug === slug);
  const project = projectsData[projectIndex];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!project) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-outfit text-4xl font-bold text-zinc-50 mb-4">404</h1>
          <p className="text-zinc-400 mb-8">{lang === 'en' ? 'Project not found' : 'Projet introuvable'}</p>
          <Link to="/" data-testid="back-home-404" className="bg-white text-black px-6 py-3 text-sm font-semibold uppercase tracking-wider hover:bg-zinc-200 transition-colors">
            {lang === 'en' ? 'Back to Home' : 'Retour a l\'accueil'}
          </Link>
        </div>
      </div>
    );
  }

  const data = project[lang];
  const nextProject = projectsData[(projectIndex + 1) % projectsData.length];
  const prevProject = projectsData[(projectIndex - 1 + projectsData.length) % projectsData.length];

  return (
    <div className="bg-zinc-950 min-h-screen">
      <Helmet>
        <title>{`${data.title} - MadisonFred`}</title>
        <meta name="description" content={data.tagline} />
      </Helmet>

      {/* Sticky header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-zinc-950/70 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 flex items-center justify-between h-16 lg:h-20">
          <Link to="/" data-testid="project-logo" className="shrink-0">
            <img src="https://customer-assets.emergentagent.com/job_madison-fred-design/artifacts/zy0qofhd_Copilot_20260414_133202.png" alt="MadisonFred" className="h-10 lg:h-12 w-auto" />
          </Link>
          <div className="flex items-center gap-4">
            <button
              data-testid="project-lang-toggle"
              onClick={toggleLang}
              className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500 hover:text-zinc-50 transition-colors border border-white/10 px-3 py-1.5"
            >
              {lang === 'en' ? 'FR' : 'EN'}
            </button>
            <Link
              to="/"
              data-testid="project-back-btn"
              className="text-sm text-zinc-400 hover:text-zinc-50 transition-colors flex items-center gap-2"
            >
              <ArrowLeft size={16} />
              {lang === 'en' ? 'All Projects' : 'Tous les Projets'}
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="pt-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 pt-16 lg:pt-24 pb-12">
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500 mb-4"
          >
            {data.category}
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            data-testid="project-title"
            className="text-6xl sm:text-7xl lg:text-8xl font-normal leading-[1.1] text-purple-400 mb-6"
            style={{ fontFamily: "'Great Vibes', cursive" }}
          >
            {data.title}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-xl lg:text-2xl font-manrope text-zinc-400 max-w-3xl leading-relaxed"
          >
            {data.tagline}
          </motion.p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.8 }}
          className="max-w-7xl mx-auto px-6 lg:px-12"
        >
          <img
            src={project.heroImage}
            alt={data.title}
            data-testid="project-hero-image"
            className="w-full max-h-[75vh] object-cover"
          />
        </motion.div>
      </section>

      {/* Project Info Bar */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 py-16 lg:py-20">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 border-y border-white/5 py-10">
          {[
            { icon: User, label: lang === 'en' ? 'Client' : 'Client', value: project.client },
            { icon: Calendar, label: lang === 'en' ? 'Year' : 'Annee', value: project.year },
            { icon: Clock, label: lang === 'en' ? 'Duration' : 'Duree', value: project.duration },
            { icon: ArrowUpRight, label: lang === 'en' ? 'Category' : 'Categorie', value: data.category },
          ].map((item, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 + i * 0.1 }}
              data-testid={`project-info-${i}`}
            >
              <item.icon size={16} className="text-zinc-600 mb-2" />
              <p className="text-xs font-semibold uppercase tracking-[0.15em] text-zinc-500 mb-1">{item.label}</p>
              <p className="font-outfit font-semibold text-zinc-50">{item.value}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Overview */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 pb-24 lg:pb-32">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500 mb-4">
              {lang === 'en' ? 'Overview' : 'Apercu'}
            </p>
            <p className="text-base lg:text-lg font-manrope text-zinc-400 leading-relaxed">
              {data.desc}
            </p>
          </motion.div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500 mb-4">
              {lang === 'en' ? 'Tech Stack' : 'Technologies'}
            </p>
            <div className="flex flex-wrap gap-3">
              {project.tech.map((t, i) => (
                <span
                  key={i}
                  data-testid={`project-tech-${i}`}
                  className="text-sm font-manrope text-zinc-300 border border-white/10 px-4 py-2 hover:border-white/30 transition-colors"
                >
                  {t}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Challenge & Solution */}
      <section className="bg-zinc-900 border-y border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-24 lg:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              data-testid="project-challenge"
            >
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500 mb-4">
                {lang === 'en' ? 'The Challenge' : 'Le Defi'}
              </p>
              <h3 className="text-3xl lg:text-4xl font-normal tracking-normal text-purple-400 mb-6" style={{ fontFamily: "'Great Vibes', cursive" }}>
                {lang === 'en' ? 'What we faced' : 'Ce que nous avons affronte'}
              </h3>
              <p className="text-base lg:text-lg font-manrope text-zinc-400 leading-relaxed">
                {data.challenge}
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.15 }}
              data-testid="project-solution"
            >
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500 mb-4">
                {lang === 'en' ? 'The Solution' : 'La Solution'}
              </p>
              <h3 className="text-3xl lg:text-4xl font-normal tracking-normal text-purple-400 mb-6" style={{ fontFamily: "'Great Vibes', cursive" }}>
                {lang === 'en' ? 'How we solved it' : 'Comment nous l\'avons resolu'}
              </h3>
              <p className="text-base lg:text-lg font-manrope text-zinc-400 leading-relaxed">
                {data.solution}
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Results */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 py-24 lg:py-32">
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500 mb-4"
        >
          {lang === 'en' ? 'Results' : 'Resultats'}
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-5xl lg:text-6xl font-normal tracking-normal text-purple-400 mb-16"
          style={{ fontFamily: "'Great Vibes', cursive" }}
        >
          {lang === 'en' ? 'The Impact' : 'L\'Impact'}
        </motion.h2>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
          {data.results.map((r, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              data-testid={`project-result-${i}`}
              className="bg-zinc-900 border border-white/5 p-8"
            >
              <p className="font-outfit text-4xl lg:text-5xl font-black text-zinc-50 mb-2">{r.metric}</p>
              <p className="text-sm text-zinc-500 uppercase tracking-wider">{r.label}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Gallery */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 pb-24 lg:pb-32">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500 mb-8">
          {lang === 'en' ? 'Gallery' : 'Galerie'}
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {project.gallery.map((img, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className={i === 0 ? 'md:col-span-2' : ''}
            >
              <img
                src={img}
                alt={`${data.title} ${i + 1}`}
                data-testid={`project-gallery-${i}`}
                className="w-full object-cover max-h-[60vh]"
              />
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-24 lg:py-32 text-center">
          <h2 className="text-4xl lg:text-5xl font-normal tracking-normal text-purple-400 mb-6" style={{ fontFamily: "'Great Vibes', cursive" }}>
            {lang === 'en' ? 'Have a similar project?' : 'Vous avez un projet similaire ?'}
          </h2>
          <p className="text-base font-manrope text-zinc-400 mb-10 max-w-lg mx-auto">
            {lang === 'en' ? 'Let\'s discuss how we can help bring your vision to life.' : 'Discutons de comment nous pouvons donner vie a votre vision.'}
          </p>
          <Link
            to="/#contact"
            data-testid="project-cta"
            className="inline-flex items-center gap-3 bg-white text-black px-8 py-4 text-sm font-semibold uppercase tracking-wider hover:bg-zinc-200 transition-colors"
          >
            {lang === 'en' ? 'Start a Project' : 'Demarrer un Projet'}
            <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      {/* Next/Prev Navigation */}
      <section className="border-t border-white/5" data-testid="project-navigation">
        <div className="grid grid-cols-2">
          <Link
            to={`/project/${prevProject.slug}`}
            data-testid="project-prev"
            className="group border-r border-white/5 px-6 lg:px-12 py-12 lg:py-16 hover:bg-zinc-900 transition-colors"
          >
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-600 mb-2 flex items-center gap-2">
              <ArrowLeft size={14} />
              {lang === 'en' ? 'Previous' : 'Precedent'}
            </p>
            <p className="font-outfit text-xl lg:text-2xl font-bold text-zinc-400 group-hover:text-zinc-50 transition-colors tracking-tight">
              {prevProject[lang].title}
            </p>
          </Link>
          <Link
            to={`/project/${nextProject.slug}`}
            data-testid="project-next"
            className="group px-6 lg:px-12 py-12 lg:py-16 hover:bg-zinc-900 transition-colors text-right"
          >
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-600 mb-2 flex items-center gap-2 justify-end">
              {lang === 'en' ? 'Next' : 'Suivant'}
              <ArrowRight size={14} />
            </p>
            <p className="font-outfit text-xl lg:text-2xl font-bold text-zinc-400 group-hover:text-zinc-50 transition-colors tracking-tight">
              {nextProject[lang].title}
            </p>
          </Link>
        </div>
      </section>
    </div>
  );
}
